import { notifyIncomingChatMessage } from '@/lib/notifications/chat-message-notifications';
import { getAuthToken, getAuthUser, subscribeAuthStateChange } from '@/lib/api/token-store';
import { startChatRealtime, stopChatRealtime, subscribeChatMessages } from '@/lib/realtime/chat-realtime-service';

let installed = false;
let incomingNotifyInstalled = false;
// Identity the current socket was started for. Token *presence* is not enough: a profile
// switch swaps token A for token B while both are truthy, and startChatRealtime() no-ops on
// a live socket — without this check user A's authenticated socket (and A's me/queue
// subscriptions) would keep streaming into user B's session.
let lastUserId = '';

function installIncomingMessageNotifications() {
  if (incomingNotifyInstalled) {
    return;
  }
  incomingNotifyInstalled = true;
  subscribeChatMessages((detail) => {
    if (detail.kind !== 'message') {
      return;
    }
    // In-place refreshes (reactions, edits, unsend/revoke verdicts) re-emit an EXISTING message —
    // never a new arrival to announce. (Known gap: an inbound PATIENT WhatsApp edit is re-published
    // as a regular message with the same id and still notifies.)
    if (detail.update === true) {
      return;
    }
    void notifyIncomingChatMessage(detail.message);
  });
}

function syncRealtimeWithAuth() {
  const token = String(getAuthToken() || '').trim();
  const userId = String(getAuthUser()?.id || '').trim();
  if (!token) {
    lastUserId = '';
    stopChatRealtime();
    return;
  }
  if (userId !== lastUserId) {
    // Identity changed under a live token: tear the old socket down so the restart connects
    // with the new token and the connect-time subscriptions re-read the new getAuthUser().
    stopChatRealtime();
  }
  lastUserId = userId;
  startChatRealtime();
}

/**
 * Keeps the chat WebSocket lifecycle aligned with auth (connect after login, disconnect on
 * logout, reconnect as the new user on a profile switch). Safe to call once from the app
 * root (idempotent).
 */
export function registerAuthRealtimeBridge() {
  if (installed) {
    return;
  }
  installed = true;
  // Both builds; the notifier decides per message. Web mode never prompts from this subscription
  // (a permission request outside a click is refused) — it stays silent until the user enables
  // notifications from the sidebar prompt or Settings ▸ Preferences.
  installIncomingMessageNotifications();
  subscribeAuthStateChange(syncRealtimeWithAuth);
  syncRealtimeWithAuth();
}
